import { useCallback, useRef, useState } from 'react';
import { errorMessage } from './api.ts';

export type ToastTone = 'info' | 'error';

export type ToastOptions = {
  tone?: ToastTone;
  /** Runs the inverse operation. Renders an Undo button — or `actionLabel`'s
   *  text, for the cases where the button is a plain action rather than an
   *  undo. */
  undo?: (() => unknown) | null;
  actionLabel?: string | null;
};

/** The live toast. `undoing` is true while `undo` is in flight, so the button
 *  can disable itself rather than firing the inverse twice. */
export type Toast = {
  id: number;
  message: string;
  tone: ToastTone;
  undo: (() => unknown) | null;
  actionLabel: string | null;
  undoing: boolean;
};

export type ToastApi = {
  toast: Toast | null;
  pushToast: (message: string, opts?: ToastOptions) => void;
  dismissToast: () => void;
  runUndo: () => Promise<void>;
};

/**
 * One transient toast at a time — a new push replaces the current one.
 * Structural mutations (reorder, delete, recolour…) confirm through these;
 * an optional `undo` callback renders an Undo button that runs the inverse
 * operation — the only "that wasn't what I meant" path, so destructive
 * actions that can't be undone confirm first instead.
 */
export function useToast(): ToastApi {
  const [toast, setToast] = useState<Toast | null>(null);
  // Mirror of the current toast — runUndo must read it synchronously, and
  // state updaters only run during the next render.
  const currentRef = useRef<Toast | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idRef = useRef(0);

  const update = useCallback((next: Toast | null) => {
    currentRef.current = next;
    setToast(next);
  }, []);

  const dismissToast = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    update(null);
  }, [update]);

  const pushToast = useCallback(
    (message: string, opts: ToastOptions = {}) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const id = ++idRef.current;
      update({
        id,
        message,
        tone: opts.tone || 'info', // 'info' | 'error'
        undo: opts.undo || null, // async () => void — runs the inverse op
        actionLabel: opts.actionLabel || null, // button label when `undo` is a generic action (e.g. 'View')
        undoing: false,
      });
      // Errors and undoable actions linger a little longer.
      const ttl = opts.tone === 'error' || opts.undo ? 6000 : 3500;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (currentRef.current?.id === id) update(null);
      }, ttl);
    },
    [update],
  );

  const runUndo = useCallback(async () => {
    const entry = currentRef.current;
    if (!entry?.undo || entry.undoing) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    update({ ...entry, undoing: true });
    try {
      await entry.undo();
      update(null);
    } catch (err) {
      pushToast(errorMessage(err) || 'Undo failed', { tone: 'error' });
    }
  }, [update, pushToast]);

  return { toast, pushToast, dismissToast, runUndo };
}
